import { Editor, Range, Element as SlateElement, Transforms } from "slate";
import { ListsEditor, ListType } from "@prezly/slate-lists";
import { ReactEditor } from "slate-react";
import { removeLink } from "../plugins/links/link-utilities";
export const SLATE_ZERO_WIDTH_SPACE = "\uFEFF";
export const TYPE_STYLE_TYPES = [
    "headingOne",
    "headingTwo",
    "headingThree",
    "headingFour",
    "headingFive",
    "headingSix",
    "paragraph",
    "quote"
];
const BLOCK_TYPES = [
    "code",
    "horizontalRule",
    "headingOne",
    "headingTwo",
    "headingThree",
    "headingFour",
    "headingFive",
    "headingSix",
    "link",
    "orderedList",
    "paragraph",
    "quote",
    "unorderedList"
];
export const FONT_SIZES = [10, 12, 14, 16, 18, 24, 32, 48];
export const FONT_FAMILY = ["Helvetica", "Arial", "Georgia", "Impact", "Tahoma", "Times New Roman"];
export const getFormattingType = (controlName) => {
    switch (controlName) {
        case "bold":
        case "italic":
        case "strikeThrough":
        case "underline":
            return "mark";
        case "orderedList":
        case "unorderedList":
            return "block";
        default:
            return undefined;
    }
};
export const isMarkActive = (editor, format) => {
    const marks = Editor.marks(editor);
    return marks ? marks[format] === true : false;
};
export const isBlockActive = (editor, format) => {
    if (!editor.selection) {
        return false;
    }
    const [match] = Array.from(Editor.nodes(editor, {
        at: Editor.unhangRange(editor, editor.selection),
        match: (n) => !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === format
    }));
    return Boolean(match);
};
export const isActiveTextAlign = (editor, textAlign) => {
    if (!editor.selection) {
        return false;
    }
    const [match] = Array.from(Editor.nodes(editor, {
        at: Editor.unhangRange(editor, editor.selection),
        // @ts-expect-error needs an isAlignable filter or something
        match: (n) => Editor.isBlock(editor, n) && n.textAlign === textAlign
    }));
    return Boolean(match);
};
export const isButtonActive = (editor, controlName) => {
    const controlNameType = getFormattingType(controlName);
    if (controlNameType === "mark") {
        return isMarkActive(editor, controlName);
    }
    if (controlNameType === "block") {
        return isBlockActive(editor, controlName);
    }
    return undefined; // avoid announcing "not pressed" for items that don't have an active state in general
};
export const toggleFormat = (editor, controlName) => {
    const controlNameType = getFormattingType(controlName);
    if (controlNameType === "mark") {
        return toggleMark(editor, controlName);
    }
    if (controlNameType === "block") {
        return toggleBlock(editor, controlName);
    }
};
export const toggleMark = (editor, format) => {
    const isActive = isMarkActive(editor, format);
    if (isActive) {
        Editor.removeMark(editor, format);
    }
    else {
        Editor.addMark(editor, format, true);
    }
};
export const setColor = (editor, color) => {
    Editor.addMark(editor, "color", color);
};
export const getSelectedColor = (editor) => {
    const marks = Editor.marks(editor);
    return marks && marks.color ? marks.color : "";
};
export const isFontFamilyActive = (editor, fontFamily) => {
    const marks = Editor.marks(editor);
    return marks ? marks.fontFamily === fontFamily : false;
};
export const setFontFamily = (editor, fontFamily) => {
    Editor.addMark(editor, "fontFamily", fontFamily);
};
export const isFontSizeActive = (editor, fontSize) => {
    const marks = Editor.marks(editor);
    return marks ? marks.fontSize === fontSize : false;
};
export const setFontSize = (editor, fontSize) => {
    Editor.addMark(editor, "fontSize", fontSize);
};
export const indent = (editor) => {
    ListsEditor.increaseDepth(editor);
};
export const outdent = (editor) => {
    ListsEditor.decreaseDepth(editor);
};
export const setTextAlign = (editor, textAlign) => {
    Transforms.setNodes(editor, { textAlign });
};
const getEditorRange = (editor) => {
    // editor.children can sometimes be undefined, even though TypeScript says otherwise
    if (!editor.children || editor.children.length === 0) {
        return undefined;
    }
    return {
        anchor: Editor.start(editor, [0]),
        focus: Editor.end(editor, [editor.children.length - 1])
    };
};
const isSelectionValid = (editor) => {
    if (!editor.selection) {
        return false;
    }
    const editorRange = getEditorRange(editor);
    if (!editorRange) {
        return false;
    }
    return Range.includes(editorRange, editor.selection);
};
const getCurrentNodeEntry = (editor) => {
    if (!editor.selection || !isSelectionValid(editor)) {
        return null;
    }
    return Editor.node(editor, editor.selection, { depth: 1 });
};
export const getCurrentList = (editor) => {
    if (!editor.selection) {
        return;
    }
    const [currentList] = ListsEditor.getParentList(editor, editor.selection.focus?.path) || [];
    return currentList;
};
export const toggleList = (editor, type) => {
    if (!editor.selection) {
        return;
    }
    const currentList = getCurrentList(editor);
    const listType = type === "orderedList" ? ListType.ORDERED : ListType.UNORDERED;
    if (currentList) {
        const shouldToggleOff = type === currentList.type;
        if (shouldToggleOff) {
            ListsEditor.unwrapList(editor);
        }
        else if (type.includes("List")) {
            ListsEditor.setListType(editor, listType);
        }
        else {
            ListsEditor.wrapInList(editor, listType);
        }
    }
    else {
        ListsEditor.wrapInList(editor, listType);
    }
};
export const toggleBlock = (editor, type) => {
    const [currentNode] = getCurrentNodeEntry(editor) || [];
    if (!currentNode || !SlateElement.isElement(currentNode)) {
        return;
    }
    ListsEditor.unwrapList(editor);
    Transforms.setNodes(editor, { type });
};
export const insertLineBreak = (editor) => {
    Transforms.insertText(editor, "\n");
};
export const clearStyles = (editor) => {
    const clearMarks = (marks) => {
        if (marks && Object.keys(marks).length) {
            Object.keys(marks).forEach((key) => Editor.removeMark(editor, key));
            clearMarks(Editor.marks(editor));
        }
    };
    clearMarks(Editor.marks(editor));
    removeLink(editor, { location: editor.selection || undefined });
};
export const getDefaultValue = () => [{ type: "paragraph", children: [{ text: "" }] }];
export const focusEditor = (editor) => {
    // Don't jump scroll on focus. Not sure why this is needed since ReactEditor.focus
    // does pass {preventScroll: true}}), but the page IS jumping without it.
    // https://github.com/ianstormtaylor/slate/blob/main/packages/slate-react/src/plugin/react-editor.ts#L426
    const scrollPosition = window.scrollY;
    ReactEditor.focus(editor);
    window.scrollY = scrollPosition;
};
